/* ============ game state machine ============
   Pure transitions: every exported action takes (state, settings, ...args)
   and returns a NEW state. No React, no randomness outside the seeded
   shuffle — the same seed + the same action sequence always replays to the
   same result, which is what makes server-side validation possible later. */

import { clone, signed, fmt, makeShoeId } from "./util.js";
import { makeSeed, freshShoe, hv, upVal, pairRank, isBJ } from "./cards.js";
import { cardValue, irc, BALANCED, trueCount, recommendedUnits } from "./counting.js";
import { recommendCount, insuranceIndex } from "./deviations.js";
import { ACTION_NAME } from "./strategy.js";

// v5: deck-conditional basic strategy (1/2-deck exception cells), honest
// deviation labeling, 8,8vA H17+LS = surrender-else-split. Grading changed,
// so v4 rows can no longer be validated by this engine (version gate in
// validate-shoe) - deploy client + validate-shoe together.
// v6 (multiplayer Phase 5): per-seat economics. `deal` gains an optional
// `bets: []` (per-seat; `bet` stays legal as the uniform shorthand, so solo
// streams are unchanged), and insurance becomes PER SEAT — the event is now
// `{t:"ins", takes: []}` (one boolean per seat), each seat pays/collects on
// its own bet and is graded as its own seat-tagged log row. Replay semantics
// changed, so v5 rows can no longer be validated by this engine — same
// deploy discipline: client + validate-shoe together.
export const ENGINE_VERSION = 6;

export const DEFAULT_SETTINGS = {
  decks: 6, h17: true, das: true, surrender: true, rsa: false,
  maxSplitHands: 4, bjPay: 1.5, system: "Hi-Lo",
  pen: 75, insurance: true, unit: 25, spread: 8, coach: true,
  checkReveals: true,
};

/* ---------- state ---------- */
/* Server-issued seeds (finding C2) are buffered in game state as `seedQueue`.
   Pull the next one; if the buffer is empty (signed out / offline / not yet
   fetched) fall back to a LOCAL seed with a null signature — that shoe stays
   playable but will never validate.

   Seeds within SEED_EXP_MARGIN_MS of their signature expiry are discarded:
   the validator rejects expired signatures at completion time, so starting a
   shoe on a nearly-expired seed would doom an honest shoe. The margin covers
   the longest realistic play-through (the resume window is 60 min). Note the
   Date.now() here never enters the event log — only the *chosen* seed is
   recorded — so replay stays deterministic. */
export const SEED_EXP_MARGIN_MS = 2 * 60 * 60 * 1000;

function freshSeeds(queue) {
  const now = Date.now();
  return (Array.isArray(queue) ? queue : []).filter(
    (s) => s && (typeof s.exp !== "number" || s.exp - now > SEED_EXP_MARGIN_MS),
  );
}

function nextSeed(queue) {
  const q = freshSeeds(queue);
  const s = q.shift();
  if (s) return { seed: s.seed, sig: s.sig, nonce: s.nonce, exp: s.exp, queue: q };
  return { seed: makeSeed(), sig: null, nonce: null, exp: null, queue: q };
}

export function newGame(st, keep = {}) {
  const { seed, sig, nonce, exp, queue } = nextSeed(keep.seedQueue);
  return {
    engineVersion: ENGINE_VERSION,
    shoe: freshShoe(st, seed),
    shoeSeed: seed,
    shoeSig: sig,
    shoeNonce: nonce,
    shoeExp: exp,
    seedQueue: queue,
    shoeId: makeShoeId(),
    liveCoachOn: false,
    liveCoached: false,
    countAided: false,
    rc: irc(st),
    phase: "bet",
    seats: keep.seats ?? 1,
    hands: [],
    dealer: null,
    active: 0,
    bet: keep.bet ?? st.unit,
    bets: null,                 // v6: optional per-seat bets (null → uniform `bet`)
    bankroll: keep.bankroll ?? 1000,
    stats: keep.stats ?? { rounds: 0, decOk: 0, decTot: 0, ccOk: 0, ccTot: 0 },
    insured: false,
    insTotal: 0,
    insTakes: null,             // v6: per-seat insurance answers for the round
    insPremiums: null,          // v6: per-seat premiums (0 for declined seats)
    insNetBySeat: null,         // v6: per-seat insurance net, written at settle
    lastNet: 0,
    handSeq: 0, // monotonically-unique hand ids (uid) — stable React keys across splits
    log: [],
    events: [],
    shoeHist: keep.shoeHist ?? [],
    shoeNet: 0,
    msg: "Set your bet and deal",
    coach: null,
    shuffled: true,
  };
}

/* seat-aware hand names: a lone hand is "Hand 2"; split hands "Hand 2a"… */
export function labelFor(hands, idx) {
  const h = hands[idx];
  const sibs = hands.filter((x) => x.seat === h.seat);
  if (sibs.length === 1) return `Hand ${h.seat + 1}`;
  let pos = 0;
  for (let j = 0; j <= idx; j++) if (hands[j].seat === h.seat) pos++;
  return `Hand ${h.seat + 1}${String.fromCharCode(96 + pos)}`;
}

function seatCount(d, seat) {
  return d.hands.filter((h) => h.seat === seat).length;
}

export function avail(d, h, st) {
  const two = h.cards.length === 2;
  return {
    canHit: !h.splitAces,
    canSplit:
      two &&
      pairRank(h.cards[0]) === pairRank(h.cards[1]) &&
      seatCount(d, h.seat) < st.maxSplitHands &&
      (!h.splitAces || st.rsa),
    canDouble: two && !h.splitAces && (!h.fromSplit || st.das),
    canSurrender: st.surrender && two && !h.fromSplit,
  };
}

/* ---------- internal helpers (mutate the draft) ---------- */
const tcOf = (d, st) => (BALANCED[st.system] ? trueCount(d) : null);

/* Draw one card, or fail loudly. An extreme config (1 deck · 90% pen · 3
   seats leaves a ~5-card buffer) can exhaust the shoe mid-round; without this
   guard that surfaced as a cryptic TypeError deep in cardValue. The throw is
   deterministic, so replay treats such a record the same way (invalid). */
function drawCard(d) {
  const c = d.shoe.pop();
  if (!c) {
    throw new Error(
      "shoe exhausted mid-round — penetration/seats leave too small a buffer for this deck count",
    );
  }
  return c;
}

function dealToHand(d, st, hand) {
  const c = drawCard(d);
  hand.cards.push(c);
  d.rc += cardValue(st, c);
  return c;
}

function revealHole(d, st) {
  if (!d.dealer.holeUp) {
    d.dealer.holeUp = true;
    d.rc += cardValue(st, d.dealer.cards[1]);
  }
}

function grade(d, st, chosen) {
  const h = d.hands[d.active];
  const up = d.dealer.cards[0];
  const tc = tcOf(d, st);
  const res = recommendCount(h, upVal(up), avail(d, h, st), st, tc);
  d.stats.decTot++;
  const correct = res.action === chosen;
  if (correct) d.stats.decOk++;
  d.coach = !correct && st.coach
    ? res.deviation
      ? `Index play — ${res.label} (idx ${signed(res.idx)}, TC ${signed(+tc.toFixed(1))}): ${ACTION_NAME[res.action]}`
      : `Basic strategy says: ${ACTION_NAME[res.action]}`
    : null;
  const hvv = hv(h.cards);
  // pair rank (ten-values normalize to "10"), null for non-pairs — lets the
  // real-play aggregation key pairs like the drills ("10,10 v 5") and tell soft
  // from hard, for the knowledge-vs-execution gap (B7).
  const pr =
    h.cards.length === 2 && pairRank(h.cards[0]) === pairRank(h.cards[1])
      ? pairRank(h.cards[0])
      : null;
  d.log.push({
    n: d.log.length + 1,
    round: d.stats.rounds + 1,
    // Which seat acted (multiplayer Phase 4): lets a session player's Review
    // filter to their own graded decisions. Purely additive — replay ignores
    // it and old rows simply lack it, so no ENGINE_VERSION bump.
    seat: h.seat,
    hand: h.cards.map((c) => c.r + c.s).join(" "),
    total: hvv.total,
    soft: hvv.soft,
    pair: pr,
    up: up.r + up.s,
    action: chosen,
    rec: res.action,
    correct,
    dev: res.deviation ? res.label : null,
    rc: d.rc,
    tc: tc === null ? null : +tc.toFixed(1),
  });
}

function proceed(d, st) {
  while (d.active < d.hands.length) {
    const h = d.hands[d.active];
    if (!h.done) {
      if (h.cards.length === 1) {
        dealToHand(d, st, h);
        if (
          h.splitAces &&
          !(st.rsa && h.cards[1].r === "A" && seatCount(d, h.seat) < st.maxSplitHands)
        ) h.done = true;
        else if (hv(h.cards).total === 21) h.done = true;
      }
      if (!h.done) {
        d.msg = d.hands.length > 1 ? `${labelFor(d.hands, d.active)} — your move` : "Your move";
        return;
      }
    }
    d.active++;
  }
  dealerTurn(d, st);
}

function dealerTurn(d, st) {
  revealHole(d, st);
  const live = d.hands.some((h) => !h.busted && !h.surrendered && !h.bj);
  if (live) {
    while (true) {
      const v = hv(d.dealer.cards);
      if (v.total < 17 || (v.total === 17 && v.soft && st.h17)) {
        const c = drawCard(d);
        d.dealer.cards.push(c);
        d.rc += cardValue(st, c);
      } else break;
    }
  }
  settle(d, st, false);
}

function settle(d, st, dealerBJ) {
  if (dealerBJ) revealHole(d, st);
  const dv = hv(d.dealer.cards).total;
  let net = 0;
  for (const h of d.hands) {
    if (h.surrendered) { h.net = -h.bet / 2; h.res = "SURRENDER"; }
    else if (h.bj) {
      if (dealerBJ) { h.net = 0; h.res = "PUSH"; }
      else { h.net = h.bet * st.bjPay; h.res = "BLACKJACK"; }
    }
    else if (dealerBJ) { h.net = -h.bet; h.res = "LOSE"; }
    else if (h.busted) { h.net = -h.bet; h.res = "BUST"; }
    else if (dv > 21) { h.net = h.bet; h.res = "WIN"; }
    else if (hv(h.cards).total > dv) { h.net = h.bet; h.res = "WIN"; }
    else if (hv(h.cards).total < dv) { h.net = -h.bet; h.res = "LOSE"; }
    else { h.net = 0; h.res = "PUSH"; }
    net += h.net;
  }
  // v6: settle insurance per seat (2:1 on each seat's own premium) and keep
  // the per-seat nets — session recaps/saves attribute insurance exactly.
  if (d.insured) {
    d.insNetBySeat = {};
    for (let s = 0; s < d.insPremiums.length; s++) {
      const p = d.insPremiums[s];
      if (!p) continue;
      const w = dealerBJ ? p * 2 : -p;
      d.insNetBySeat[s] = w;
      net += w;
    }
  }
  d.bankroll += net;
  d.lastNet = net;
  d.shoeNet += net;
  d.stats.rounds++;
  d.phase = "done";
  // Surface the insurance result so the round message reconciles with the
  // per-hand badge: e.g. a hand can show "LOSE -$150" while insurance pays $150
  // and the round nets even — without this it reads like a contradiction.
  const parts = [];
  if (dealerBJ) parts.push("Dealer blackjack.");
  if (d.insured) {
    parts.push(dealerBJ ? `Insurance pays $${fmt(d.insTotal * 2)}.` : `Insurance loses $${fmt(d.insTotal)}.`);
  }
  parts.push(
    net > 0 ? `You win $${fmt(net)}.`
      : net < 0 ? `You lose $${fmt(-net)}.`
        : d.insured ? "Net even — no change." : "Push — no change.",
  );
  d.msg = parts.join(" ");
}

function peekResolve(d, st) {
  const dUp = upVal(d.dealer.cards[0]);
  if ((dUp === 10 || dUp === 11) && isBJ(d.dealer.cards)) {
    settle(d, st, true);
    return;
  }
  d.active = 0;
  d.phase = "play";
  proceed(d, st);
}

/* ---------- exported actions: (state, settings, ...args) → new state ---------- */
export function deal(g, st) {
  const d = clone(g);
  d.coach = null;
  d.lastNet = 0;
  const totalCards = st.decks * 52;

  if (d.shoe.length < totalCards * (1 - st.pen / 100)) {
    if (d.log.length) {
      d.shoeHist.push({
        id: d.shoeHist.length + 1,
        shoeId: d.shoeId,
        engineVersion: ENGINE_VERSION,
        seed: d.shoeSeed,
        seedSig: d.shoeSig,
        seedNonce: d.shoeNonce,
        seedExp: d.shoeExp,
        system: st.system,
        decks: st.decks,
        settings: { ...st },
        ok: d.log.filter((x) => x.correct).length,
        total: d.log.length,
        net: d.shoeNet,
        decisions: d.log,
        events: d.events,
        liveCoached: d.liveCoached,
        countAided: d.countAided,
      });
    }
    d.log = [];
    d.events = [];
    d.shoeNet = 0;
    // Each new shoe is independent: coaching resets OFF (a shoe is tainted only
    // if you turn coaching on *during* it), and the bet resets to one unit
    // (the count is neutral at a fresh shoe).
    d.liveCoachOn = false;
    d.liveCoached = false;
    d.countAided = false;
    d.bet = st.unit;
    d.bets = null; // fresh shoe: per-seat bets reset with the uniform bet
    const ns = nextSeed(d.seedQueue);
    d.shoeSeed = ns.seed;
    d.shoeSig = ns.sig;
    d.shoeNonce = ns.nonce;
    d.shoeExp = ns.exp;
    d.seedQueue = ns.queue;
    d.shoeId = makeShoeId();
    d.shoe = freshShoe(st, d.shoeSeed);
    d.rc = irc(st);
    d.shuffled = true;
  } else d.shuffled = false;

  // v6: per-seat bets when set (session play / future solo UI), else the
  // uniform `bet` — and the event carries exactly what replay needs.
  const perSeat = Array.isArray(d.bets) && d.bets.length === d.seats;
  const seatBets = perSeat
    ? [...d.bets]
    : Array.from({ length: d.seats }, () => d.bet);
  d.events.push({
    t: "deal", bet: d.bet, seats: d.seats,
    ...(perSeat ? { bets: [...seatBets] } : {}),
  });

  d.hands = Array.from({ length: d.seats }, (_, i) => ({
    uid: ++d.handSeq || (d.handSeq = 1),
    seat: i, cards: [], bet: seatBets[i], fromSplit: false, splitAces: false,
    done: false, busted: false, surrendered: false, bj: false, doubled: false,
    res: null, net: 0,
  }));
  d.insured = false;
  d.insTotal = 0;
  d.insTakes = null;
  d.insPremiums = null;
  d.insNetBySeat = null;

  for (const h of d.hands) dealToHand(d, st, h);
  const up = drawCard(d);
  d.rc += cardValue(st, up);
  for (const h of d.hands) dealToHand(d, st, h);
  const hole = drawCard(d); // not counted until revealed
  d.dealer = { cards: [up, hole], holeUp: false };

  for (const h of d.hands) if (isBJ(h.cards)) { h.bj = true; h.done = true; }

  if (up.r === "A" && st.insurance) {
    d.phase = "insurance";
    d.msg = "Dealer shows an ace — insurance?";
  } else {
    peekResolve(d, st);
  }
  return d;
}

/* v6: insurance is PER SEAT. `takes` may be an array (one boolean per seat)
   or a plain boolean, which applies to every seat — so solo call sites keep
   passing true/false unchanged. The EVENT is always the canonical array
   (`{t:"ins", takes:[]}`), each seat pays premium = its own bet / 2, and each
   seat's decision is graded as its own seat-tagged log row. */
export function answerInsurance(g, st, takes) {
  const d = clone(g);
  const arr = Array.from({ length: d.seats }, (_, s) =>
    Array.isArray(takes) ? !!takes[s] : !!takes,
  );
  d.events.push({ t: "ins", takes: arr });
  // At the insurance phase no splits have happened yet: hands[s].seat === s.
  d.insPremiums = arr.map((take, s) => (take ? d.hands[s].bet / 2 : 0));
  d.insTakes = arr;
  d.insured = arr.some(Boolean);
  d.insTotal = d.insPremiums.reduce((a, p) => a + p, 0);
  const tc = tcOf(d, st);
  if (tc !== null) {
    const insIdx = insuranceIndex(st.system) ?? 3;
    const should = tc >= insIdx;
    const up = d.dealer.cards[0];
    let anyWrong = false;
    for (let s = 0; s < arr.length; s++) {
      const correct = arr[s] === should;
      d.stats.decTot++;
      if (correct) d.stats.decOk++;
      else anyWrong = true;
      d.log.push({
        n: d.log.length + 1, round: d.stats.rounds + 1,
        seat: s,
        hand: "insurance", total: null, up: up.r + up.s,
        action: arr[s] ? "INS" : "NO", rec: should ? "INS" : "NO",
        correct, dev: "Insurance", rc: d.rc, tc: +tc.toFixed(1),
      });
    }
    d.coach = anyWrong && st.coach
      ? should
        ? `Index play — insurance pays at TC ≥ ${signed(insIdx)} (TC ${signed(+tc.toFixed(1))}): take it`
        : `Insurance is only worth it at TC ≥ ${signed(insIdx)} (TC ${signed(+tc.toFixed(1))}): decline`
      : null;
  }
  peekResolve(d, st);
  return d;
}

export function hit(g, st) {
  const d = clone(g);
  d.events.push({ t: "H" });
  grade(d, st, "H");
  const h = d.hands[d.active];
  dealToHand(d, st, h);
  const t = hv(h.cards).total;
  if (t > 21) { h.busted = true; h.done = true; }
  else if (t === 21) h.done = true;
  if (h.done) proceed(d, st);
  return d;
}

export function stand(g, st) {
  const d = clone(g);
  d.events.push({ t: "S" });
  grade(d, st, "S");
  d.hands[d.active].done = true;
  proceed(d, st);
  return d;
}

export function doubleDown(g, st) {
  const d = clone(g);
  d.events.push({ t: "D" });
  grade(d, st, "D");
  const h = d.hands[d.active];
  h.bet *= 2;
  h.doubled = true;
  dealToHand(d, st, h);
  if (hv(h.cards).total > 21) h.busted = true;
  h.done = true;
  proceed(d, st);
  return d;
}

export function split(g, st) {
  const d = clone(g);
  d.events.push({ t: "P" });
  grade(d, st, "P");
  const h = d.hands[d.active];
  const aces = h.cards[0].r === "A";
  const mk = (c) => ({
    uid: ++d.handSeq || (d.handSeq = 1), // resumed pre-v5.1 snapshots lack handSeq
    seat: h.seat, cards: [c], bet: h.bet, fromSplit: true, splitAces: aces,
    done: false, busted: false, surrendered: false, bj: false, doubled: false,
    res: null, net: 0,
  });
  d.hands.splice(d.active, 1, mk(h.cards[0]), mk(h.cards[1]));
  proceed(d, st);
  return d;
}

export function surrender(g, st) {
  const d = clone(g);
  d.events.push({ t: "R" });
  grade(d, st, "R");
  const h = d.hands[d.active];
  h.surrendered = true;
  h.done = true;
  proceed(d, st);
  return d;
}

/* ---------- bets, seats, misc ---------- */
export function addBet(g, _st, amount) {
  const d = clone(g);
  d.bet += amount;
  return d;
}

export function resetBet(g, st) {
  const d = clone(g);
  d.bet = st.unit;
  return d;
}

/* v6: set per-seat bets for the NEXT deal (session play: the host collects
   each seat's chosen bet into this before dealing; a future solo UI could use
   it too). null clears back to the uniform `bet`. Local-only like addBet —
   the deal EVENT carries the authoritative bets. */
export function setBets(g, _st, bets) {
  const d = clone(g);
  d.bets = Array.isArray(bets) ? bets.map((b) => +b) : null;
  return d;
}

export function toBetPhase(g) {
  const d = clone(g);
  d.phase = "bet";
  d.msg = "Set your bet and deal";
  return d;
}

export function addSeat(g) {
  const d = clone(g);
  if (d.seats < 3) d.seats++;
  return d;
}

export function dropSeat(g, _st, seat) {
  const d = clone(g);
  if (d.seats <= 1) return d;
  // Mid-round drops would desync seats from hands (the abandoned seat's hands
  // keep playing and settling). The UI only offers the control between
  // rounds; the engine guards regardless.
  if (d.phase !== "bet" && d.phase !== "done") return d;
  d.seats--;
  if (d.phase === "done") d.hands = d.hands.filter((h) => h.seat !== seat);
  return d;
}

export function tallyCountCheck(g, _st, ok) {
  const d = clone(g);
  d.stats.ccTot++;
  if (ok) d.stats.ccOk++;
  return d;
}

/* Live Coaching (Feature 2): the on-board coach toggle. Latches `liveCoached`
   true the moment it's ever switched on (including being on at shoe start);
   turning it back off does NOT clear the taint. A shoe with liveCoached = true
   never validates for the leaderboard. */
export function setLiveCoach(g, _st, on) {
  const d = clone(g);
  d.liveCoachOn = !!on;
  if (on) d.liveCoached = true;
  return d;
}

/* Count aid (Feature 3): latch `countAided` true the moment the player sees the
   running count mid-shoe — via "Reveal" or a revealing "Check". Like liveCoached
   it never clears once set, and resets each shoe. Gates ranked eligibility
   (see LEADERBOARD-DESIGN.md). */
export function markCountAided(g) {
  const d = clone(g);
  d.countAided = true;
  return d;
}

/* Blind count submit: record a running-count guess into the signed `events`
   stream so validate-shoe can re-verify it on replay (this is what makes a
   counting-accuracy metric trustworthy). Recording alone never reveals the
   answer or taints the shoe — the UI decides whether to reveal (reveal mode)
   or stay blind.

   `seat` (multiplayer Phase 4): in a session every player's guesses enter
   the shared stream, so each is tagged with the guesser's seat. Optional and
   omitted entirely in solo play — replayShoe reads only ev.guess, so the
   extra field is replay-safe (no ENGINE_VERSION bump) and solo streams are
   byte-identical to before. */
export function recordCount(g, _st, guess, seat) {
  const d = clone(g);
  d.events.push(seat == null ? { t: "cc", guess } : { t: "cc", guess, seat });
  return d;
}

/* Append a freshly-fetched batch of server seeds to the buffer (finding C2).
   If the current shoe is still pristine (bet phase, nothing played) and is on
   a local fallback seed, upgrade it in place to a signed seed so the player's
   very first shoe of a session validates too — invisible, since no cards are
   on the table yet. */
export function addSeeds(g, st, seeds) {
  const d = clone(g);
  d.seedQueue = freshSeeds([...(d.seedQueue ?? []), ...(seeds ?? [])]);
  if (d.phase === "bet" && d.log.length === 0 && d.events.length === 0 && !d.shoeSig) {
    const ns = nextSeed(d.seedQueue);
    if (ns.sig != null) {
      d.shoeSeed = ns.seed;
      d.shoeSig = ns.sig;
      d.shoeNonce = ns.nonce;
      d.shoeExp = ns.exp;
      d.seedQueue = ns.queue;
      d.shoe = freshShoe(st, ns.seed);
      d.rc = irc(st);
    }
  }
  return d;
}

/* ---------- replay validation ----------
   Rebuilds a shoe from (seed, settings, events) and replays every event
   through the exact same engine, enforcing legality at each step. Returns
   the recomputed results for comparison against a claimed record. Throws
   on any illegal or out-of-phase event — a thrown replay means the record
   could not have been produced by honest play. Runs identically in the
   browser, Node, and Deno (Edge Functions). */
export function replayShoe(seed, st, events) {
  let g = newGame(st);
  g.shoe = freshShoe(st, seed);
  g.shoeSeed = seed;
  g.rc = irc(st);

  // Leaderboard metrics, accumulated during this trusted replay (LB Phase 2):
  //   bet correlation — sufficient stats for Pearson r of recommended (x) vs
  //     actual (y) bet units per round; counting accuracy — blind cc guesses
  //     graded against the true running count; hands — rounds played.
  const m = {
    hands: 0,
    bc_n: 0, bc_sx: 0, bc_sy: 0, bc_sxx: 0, bc_syy: 0, bc_sxy: 0,
    cc_ok: 0, cc_total: 0,
    cc_checks: [],
  };

  for (let i = 0; i < events.length; i++) {
    const ev = events[i] ?? {};
    if (ev.t === "deal") {
      if (g.phase !== "bet" && g.phase !== "done")
        throw new Error(`event ${i + 1}: deal during ${g.phase}`);
      if (typeof ev.bet !== "number" || !(ev.bet > 0))
        throw new Error(`event ${i + 1}: invalid bet`);
      if (![1, 2, 3].includes(ev.seats))
        throw new Error(`event ${i + 1}: invalid seat count`);
      // v6: optional per-seat bets — exactly one entry per seat, all positive.
      if (ev.bets !== undefined) {
        if (!Array.isArray(ev.bets) || ev.bets.length !== ev.seats)
          throw new Error(`event ${i + 1}: bets must list one bet per seat`);
        for (const b of ev.bets) {
          if (typeof b !== "number" || !(b > 0))
            throw new Error(`event ${i + 1}: invalid per-seat bet`);
        }
      }
      g = { ...g, bet: ev.bet, seats: ev.seats, bets: ev.bets ?? null };
      // bet correlation: recommended units (from the pre-deal true count) vs
      // what the player actually bet, in units. Under v6 this reads SEAT 0's
      // bet — identity for the heads-up shoes the metrics actually score
      // (ranked is 1-seat; multiplayer shoes are never validated).
      const recU = recommendedUnits(g, st);
      const actU = (ev.bets?.[0] ?? ev.bet) / (st.unit || 1);
      m.hands += 1;
      m.bc_n += 1;
      m.bc_sx += recU;
      m.bc_sy += actU;
      m.bc_sxx += recU * recU;
      m.bc_syy += actU * actU;
      m.bc_sxy += recU * actU;
      const shoesBefore = g.shoeHist.length;
      g = deal(g, st);
      if (g.shoeHist.length !== shoesBefore)
        throw new Error(`event ${i + 1}: deal crossed the cut card mid-replay`);
    } else if (ev.t === "ins") {
      if (g.phase !== "insurance")
        throw new Error(`event ${i + 1}: insurance answer outside insurance phase`);
      // v6: the canonical shape is per-seat takes — required.
      if (!Array.isArray(ev.takes) || ev.takes.length !== g.seats
          || ev.takes.some((x) => typeof x !== "boolean"))
        throw new Error(`event ${i + 1}: insurance requires one boolean per seat`);
      g = answerInsurance(g, st, ev.takes);
    } else if (ev.t === "H" || ev.t === "S" || ev.t === "D" || ev.t === "P" || ev.t === "R") {
      if (g.phase !== "play")
        throw new Error(`event ${i + 1}: ${ev.t} outside play phase`);
      const a = avail(g, g.hands[g.active], st);
      const legal = { H: a.canHit, S: true, D: a.canDouble, P: a.canSplit, R: a.canSurrender };
      if (!legal[ev.t]) throw new Error(`event ${i + 1}: illegal ${ev.t}`);
      const fn = { H: hit, S: stand, D: doubleDown, P: split, R: surrender }[ev.t];
      g = fn(g, st);
    } else if (ev.t === "cc") {
      // count-check guess — graded here against the true running count; no
      // effect on the replayed game state.
      m.cc_total += 1;
      const ccOk = Number(ev.guess) === g.rc;
      if (ccOk) m.cc_ok += 1;
      // per-checkpoint recap (Tier-2): the true running count is authoritative
      // here (server replay); revealed to the player only in Review, post-shoe.
      m.cc_checks.push({ guess: Number(ev.guess), actual: g.rc, ok: ccOk });
    } else {
      throw new Error(`event ${i + 1}: unknown event type`);
    }
  }

  if (g.phase === "play" || g.phase === "insurance")
    throw new Error("events ended mid-round");

  return {
    ok: g.log.filter((x) => x.correct).length,
    total: g.log.length,
    net: g.shoeNet,
    decisions: g.log,
    metrics: m,
  };
}
